import { Link } from "react-router-dom";
import type { Project, StateMachineState } from "@/types";
import { BookCover, type CoverTone, PressEyebrow, StatusPill } from "@/components/pullpress/PressVisuals";

type StateInfo = { label: string; tone: "draft" | "progress" | "editing" | "ready" | "published" | "blocked" };

const STATUS_MAP: Partial<Record<StateMachineState, StateInfo>> = {
  CREATED: { label: "Raw source", tone: "draft" },
  INGESTED: { label: "Source in", tone: "progress" },
  TRANSCRIBING: { label: "Transcribing", tone: "progress" },
  TRANSCRIBED: { label: "Transcribed", tone: "progress" },
  ANALYZING: { label: "Finding shape", tone: "progress" },
  ANALYZED: { label: "Shape found", tone: "progress" },
  STRUCTURING: { label: "Chapter map", tone: "progress" },
  STRUCTURED: { label: "Map ready", tone: "progress" },
  DRAFTING: { label: "Building manuscript", tone: "editing" },
  DRAFTED: { label: "Proof ready", tone: "editing" },
  AWAITING_REVIEW: { label: "Proof open", tone: "editing" },
  REVISED: { label: "Revision in", tone: "editing" },
  AUTO_ADVANCED: { label: "Proof closed", tone: "editing" },
  EDITING: { label: "Editor at work", tone: "editing" },
  EDITED: { label: "Edit complete", tone: "ready" },
  PACKAGING: { label: "Packet build", tone: "progress" },
  PACKAGED: { label: "Packet ready", tone: "ready" },
  PUBLISHING: { label: "Releasing", tone: "progress" },
  PUBLISHED: { label: "Published", tone: "published" },
  ERROR: { label: "Blocked", tone: "blocked" },
};

const COVER_TONES: CoverTone[] = ["mountain", "linen", "night", "botanical", "sea", "city"];

export function ProjectCard({ project }: { project: Project }) {
  const status = STATUS_MAP[project.state] ?? { label: project.state, tone: "draft" as const };
  const tone = coverToneFor(project.id);
  const targetWordCount = project.settings.target_word_count ?? 0;

  return (
    <Link
      to={`/project/${project.id}`}
      className="group grid min-h-[142px] grid-cols-[78px_1fr] gap-4 rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/90 p-3 shadow-as-1 backdrop-blur-2xl transition-[background-color,box-shadow] duration-as-2 hover:bg-paper-raised hover:shadow-as-2"
    >
      <div className="min-w-0">
        <BookCover title={project.title} subtitle={genreLabel(project.settings.genre)} author={project.author_name} tone={tone} className="h-[118px] w-[78px]" />
      </div>
      <div className="flex min-w-0 flex-col justify-between py-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
            <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">
              Updated {formatDate(project.updated_at?.toDate?.())}
            </span>
          </div>
          <h3 className="mt-2 truncate font-display text-xl font-semibold leading-tight text-ink-1 transition-colors group-hover:text-walnut-deep">
            {project.title}
          </h3>
          <p className="mt-1 font-ui text-sm text-ink-3">{genreLabel(project.settings.genre)}</p>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <span className="font-ui text-sm font-semibold text-ink-2">{nextCue(project.state)}</span>
          <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">
            {targetWordCount > 0 ? `${targetWordCount.toLocaleString()} words` : "Target not set"}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function AddBookCard() {
  return (
    <Link
      to="/new-project"
      className="group grid min-h-[142px] place-items-center rounded-as-3 border border-dashed border-rule-strong bg-paper-raised/70 p-5 text-center shadow-as-1 transition-colors hover:border-walnut hover:bg-paper-raised"
    >
      <div>
        <div className="mx-auto grid h-10 w-10 place-items-center rounded-as-2 border border-rule-strong bg-paper text-xl text-walnut transition-colors group-hover:border-walnut">
          +
        </div>
        <PressEyebrow className="mt-4 justify-center">Next book</PressEyebrow>
        <h3 className="mt-3 font-display text-lg font-semibold text-ink-1">Start the next run</h3>
        <p className="mt-2 max-w-[220px] font-text text-sm leading-as-read text-ink-3">
          Name the book, choose the path, then bring in the source.
        </p>
      </div>
    </Link>
  );
}

function nextCue(state: StateMachineState): string {
  if (state === "CREATED" || state === "INGESTED") return "Bring in source";
  if (state === "AWAITING_REVIEW" || state === "DRAFTED") return "Work the proof";
  if (state === "EDITED" || state === "PACKAGED") return "Check release packet";
  if (state === "PUBLISHED") return "Published";
  if (state === "ERROR") return "Needs a decision";
  return "Press running";
}

function coverToneFor(id: string): CoverTone {
  const hash = id.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return COVER_TONES[hash % COVER_TONES.length];
}

function genreLabel(genre: Project["settings"]["genre"]): string {
  return genre.replace("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(date: Date | undefined): string {
  if (!date) return "not recorded";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
